/**
 * The Propagate dialog's draft (S8 Task 8.11) — TanStack Form over `{ mode, setEntries, modifyEntries, removeEntries }`,
 * mounted per loaded root ACL. Mode, entries, live validation (`validateAclPatch`, messages verbatim; a masked merge is
 * refused when Azure would not keep the mask — row R5), the copy of `PropagateCopy`, then Start.
 */
import { useEffect, useState } from 'react';
import { useForm } from '@tanstack/react-form';
import { useStore } from '@tanstack/react-store';
import { Button } from '../../../../../components/ui/button';
import { DialogFooter } from '../../../../../components/ui/dialog';
import { Label } from '../../../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../../../components/ui/radio-group';
import { validateAclPatch } from '../../../../../core/engines/azureStorageAclValidate';
import { hnsServiceFacts } from '../../../../../core/engines/azureStorageHnsFacts';
import type { StorageAclChange, StorageHnsAccessControl } from '../../../../../types/azureStorage';
import { initialPropagateDraft, modifyMaskProblem, propagateChangeOf, PROPAGATE_MODES, type PropagateDraft, type PropagateMode } from '../aclPropagate';
import { PropagateCopy } from './PropagateCopy';
import { PropagateEntriesEditor } from './PropagateEntriesEditor';

type Props = {
  rootAcl: StorageHnsAccessControl;
  root: string;
  shortEtag?: string;
  prefill?: StorageAclChange;
  lockedReason?: string;
  blockedReason?: string;
  pending: boolean;
  onDirtyChange: (dirty: boolean) => void;
  onStart: (change: StorageAclChange) => void;
  onCancel: () => void;
};

export function PropagateAclForm({ rootAcl, root, shortEtag, prefill, lockedReason, blockedReason, pending, onDirtyChange, onStart, onCancel }: Props) {
  const [initial] = useState(() => initialPropagateDraft(rootAcl, prefill));
  const form = useForm({ defaultValues: initial });
  const draft = useStore(form.store, (state) => state.values);
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);
  const setEntries = (next: PropagateDraft) => {
    form.setFieldValue('setEntries', next.setEntries);
    form.setFieldValue('modifyEntries', next.modifyEntries);
    form.setFieldValue('removeEntries', next.removeEntries);
  };
  useEffect(() => { onDirtyChange(dirty); }, [dirty, onDirtyChange]);
  const change = propagateChangeOf(draft);
  const validation = validateAclPatch(change);
  const problem = validation.ok === false ? validation.reason : modifyMaskProblem(change, hnsServiceFacts());
  const startReason = lockedReason ?? blockedReason ?? problem ?? (pending ? 'Starting…' : undefined);
  const showProblem = problem && !(validation.ok === false && validation.code === 'EmptyAclChange');
  return (
    <form className="grid gap-3" onSubmit={(event) => { event.preventDefault(); event.stopPropagation(); if (!startReason) onStart(change); }}>
      <RadioGroup value={draft.mode} onValueChange={(mode) => form.setFieldValue('mode', mode as PropagateMode)} disabled={!!lockedReason} className="grid gap-1" aria-label="Propagate mode">
        {PROPAGATE_MODES.map(({ mode, label }) => (
          <div key={mode} className="flex items-center gap-2 text-xs">
            <RadioGroupItem value={mode} id={`hns-propagate-mode-${mode}`} data-testid={`hns-propagate-mode-${mode}`} title={lockedReason} />
            <Label htmlFor={`hns-propagate-mode-${mode}`} className="text-xs font-normal">{label}</Label>
          </div>
        ))}
      </RadioGroup>
      <PropagateEntriesEditor root={rootAcl} draft={draft} lockedReason={lockedReason} onChange={setEntries} />
      {showProblem && <p className="text-xs text-destructive" role="alert" data-testid="hns-propagate-validation">{problem}</p>}
      <PropagateCopy root={root} shortEtag={shortEtag} change={change} rootAcl={rootAcl} />
      {/* U28: the footer stays in view while a long Replace list scrolls (the dialog is the scroll container, p-6). */}
      <DialogFooter className="sticky bottom-0 z-10 -mx-6 -mb-6 border-t border-border bg-surface-raised px-6 py-3" data-testid="hns-propagate-footer">
        <Button type="button" variant="outline" onClick={onCancel} data-testid="hns-propagate-cancel">Cancel</Button>
        <Button type="submit" variant={draft.mode === 'modify' ? 'default' : 'destructive'} disabled={!!startReason} title={startReason} data-testid="hns-propagate-start">Start</Button>
      </DialogFooter>
    </form>
  );
}
